import React from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import InfoBox from 'components/Shared/InfoBox'
import { useCampaignConfig, useCampaignConfigDispatch } from './CampaignConfigProvider'

export default function CampaignDateInputs({ inputClassNames }) {
  const { t, i18n } = useTranslation()
  const { task, editMode, form, campaign, loading } = useCampaignConfig()
  const { setCampaignForm, hasPendingTask, hasPendingPublishTask } = useCampaignConfigDispatch()
  const isStartDateInPast =
    campaign.start_date && moment(campaign.start_date).isSameOrBefore(moment(), 'day')
  const hasTask = !!task
  const isDisabled = hasTask && ['PENDING'].includes(task.status)
  const isEditing = editMode && !isDisabled
  const isReadonly = !isEditing && !isDisabled

  const handleDateChange = (key, date) => {
    setCampaignForm({ [key]: date })
  }

  const startDateClassNames = cn('cbr-input', {
    'input-readonly': isReadonly,
    'input-editing': isEditing && !isStartDateInPast,
    'input-disabled': isEditing && (isDisabled || isStartDateInPast),
  })

  const _hasPendingTask = hasPendingTask()
  const startDate = _hasPendingTask
    ? task.args.start_date || campaign.start_date
    : form.start_date || campaign.start_date || new Date()
  const endDate = _hasPendingTask
    ? task.args.end_date
    : form.end_date || campaign.end_date || new Date()

  const showingLoading = hasPendingPublishTask()

  return (
    <div className="campaign-budget-and-runtime_date-inputs d-flex flex-column justify-content-center">
      <h2 className="card-title-default d-inline-block event-header-title">
        {t('Recommendations.campaignRuntime.selectLabel')}
      </h2>
      <div className="campaign-budget-and-runtime_date-inputs__wrapper d-flex gap-2 align-items-center">
        <InfoBox
          body={
            isReadonly
              ? t('Recommendations.campaignConfig.clickToUpdate')
              : isStartDateInPast
                ? t('Recommendations.campaignConfig.editingStartDateNotAllowed')
                : ''
          }
          tooltipClassNames="cbr-input-info-box"
          withAnchor={false}
          withArrow={false}
          enabled={isReadonly || isStartDateInPast}
        >
          <div className="form-group input-cell input-cell--input input-cell--date separatePlaceholderBox mb-0">
            <DatePicker
              name="start_date"
              minDate={new Date()}
              separatePlaceholder={true}
              selected={showingLoading ? null : startDate}
              placeholderText={null}
              onChange={(date) => handleDateChange('start_date', date)}
              dateFormat="dd.MM.yyyy"
              locale={i18n.language}
              required
              autoComplete="off"
              style={{ border: 'none' }}
              selectsStart
              startDate={form.start_date || new Date()}
              endDate={form.end_date ?? new Date()}
              disabled={!isEditing || isStartDateInPast || loading}
              className={startDateClassNames}
            />
            <div
              className={cn('cbr-input-loading', {
                'cbr-input-loading--active': showingLoading,
              })}
            >
              <span>...</span>
            </div>
          </div>
        </InfoBox>
        <div className="campaign-budget-and-runtime_date-inputs__dash">-</div>
        <InfoBox
          body={isReadonly ? t('Recommendations.campaignConfig.clickToUpdate') : ''}
          tooltipClassNames="cbr-input-info-box"
          withAnchor={false}
          withArrow={false}
          enabled={isReadonly}
        >
          <div className="form-group input-cell input-cell--input input-cell--date separatePlaceholderBox mb-0">
            <DatePicker
              name="end_date"
              minDate={moment.max(moment(), moment(startDate)).toDate()}
              separatePlaceholder={true}
              selected={showingLoading ? null : endDate}
              placeholderText={null}
              onChange={(date) => handleDateChange('end_date', date)}
              dateFormat="dd.MM.yyyy"
              locale={i18n.language}
              required
              autoComplete="off"
              style={{ border: 'none' }}
              selectsEnd
              disabled={!isEditing || loading}
              className={inputClassNames}
            />
            <div
              className={cn('cbr-input-loading', {
                'cbr-input-loading--active': showingLoading,
              })}
            >
              <span>...</span>
            </div>
          </div>
        </InfoBox>
      </div>
    </div>
  )
}

CampaignDateInputs.propTypes = {
  inputClassNames: PropTypes.string.isRequired,
}
